// ConfigJson field selection, normalization, generated descriptions and legacy rebinding.
import type {
    CategorizationRule,
    DataModel,
    DataColumn,
    ExtraBreakdown,
    MetricGroup,
    MetricSection,
    ModelKey,
    PeriodIfBlank,
    OtpCondition,
    OtpRule,
    PlanConfig,
    ReportConfig,
    ReportConfigurationConfigVersionDetailDto,
    ReportConfigurationLookupOption,
    ReportFolder,
    RuleCondition,
    RuleConditionGroup,
    RuleNode,
    RuleSet,
    TripDataFieldState,
    WorkbookExcelSheetHeader,
} from "../types";
import { resolveDifferenceUnit } from "../logic/temporalPrecision";
import { detailRuleSetDisplayName, selectedDetailRuleSetIds, usesDetailRuleSetList } from "../logic/detailRuleSetSelection";
import { combinedRuleSetIds, usesCombinedRuleSets } from "../logic/combinedRuleSetSelection";
import {
    STATIC_LOOKUP_ENTITY_KEYS,
    TRIP_DATA_FIELDS,
    buildPlainEnglish,
    buildSqlForSection,
    clone,
    columnMatchesKey,
    columnsForModel,
    dataColumnSelectionKey,
    detailColumnsForModel,
    findReportColumn,
    findSelectedDetailColumn,
    findTimestampColumn,
    excelSheetsInUse,
    isCategorizationRuleReady,
    ruleValueColumn,
    isMetricReady,
    isRuleGroupNode,
    metricFilterColumns,
    metricFilterLogic,
    metricFilterNodes,
    normalizeReportingPeriodType,
    isPeriodDateExtra,
    periodDateColumn,
    reconcileExcelLayoutColumns,
    resolveOptionIds,
    ruleNodeJoin,
    timestampColumnsForModel,
} from "../reportConfigurationLogic";

const SCHEMA_VERSION = 4;

const DISPLAY_TYPE_FALLBACK: Record<string, number> = {
    count: 1,
    percentage: 2,
    detail: 3,
    otp: 4,
};

const RULE_ACTION_FALLBACK: Record<string, number> = {
    set: 1,
    include: 2,
    exclude: 3,
};

const RULE_OPERATOR_FALLBACK: Record<string, number> = {
    in: 1,
    not_in: 2,
    eq: 3,
    ne: 4,
    contains: 5,
    not_contains: 6,
    starts_with: 7,
    ends_with: 8,
    gt: 9,
    lt: 10,
    empty: 11,
    not_empty: 12,
    // Continue the sequence for the operators added with multi-condition metric filters.
    // These ids are informational: the payload is stored as JSON with no foreign key, and
    // both SQL builders switch on the operator string, so a RuleOperator lookup row that
    // does not exist yet cannot break a save. Worth adding those rows for reporting.
    gte: 13,
    lte: 14,
    between: 15,
    before: 16,
    after: 17,
    // Negated forms of the three operators that had no partner, added so an expression of
    // the shape "A OR NOT (B AND C)" can be written inside a single criteria tree via
    // De Morgan - see the note in logic/operatorCatalog.ts. Seeded by
    // db/ManualScripts/rc.seed_RuleOperator_negated_operators.sql.
    not_starts_with: 18,
    not_ends_with: 19,
    not_between: 20,
};

const LOGIC_FALLBACK: Record<string, number> = {
    and: 1,
    or: 2,
};

const OTP_MISSING_FALLBACK: Record<string, number> = {
    exclude: 1,
    late: 2,
    ontime: 3,
};

const TRIP_ACTIVITY_FALLBACK: Record<string, number> = {
    "active-only": 1,
    both: 2,
};

type LookupOptionsByKey = Record<string, ReportConfigurationLookupOption[]>;

export type BuildPayloadArgs = {
    plan: PlanConfig;
    folder: ReportFolder;
    report: ReportConfig;
    dataModels: Record<ModelKey, DataModel>;
    lookupOptionsByKey: LookupOptionsByKey;
    lookupValues: Record<string, string[]>;
    // Only used to name Co-Plan clients in the saved plain-English text. Optional: without
    // it the narrative falls back to "client 7", which is accurate but not friendly.
    clientOptions?: ReportConfigurationLookupOption[];
};

export type BuildPayloadResult = {
    payload: Record<string, unknown>;
    plan: PlanConfig;
    folder: ReportFolder;
    report: ReportConfig;
};

function lookupId(optionsByKey: LookupOptionsByKey, key: string, value: string, fallback?: number) {
    const normalized = String(value || "").trim().toLowerCase();
    const match = (optionsByKey[key] || []).find((option) =>
        String(option.value).trim().toLowerCase() === normalized
        || String(option.name).trim().toLowerCase() === normalized);
    const id = Number(match?.id);
    return Number.isFinite(id) && id > 0 ? id : fallback ?? null;
}

function logicId(logic: "and" | "or") {
    return LOGIC_FALLBACK[logic] || 1;
}

function sortIndex(sortOrder: number) {
    return Math.max(1, Math.floor((sortOrder || 10) / 10));
}

function nonEmptyText(value: string | undefined | null, fallback: string) {
    const text = String(value || "").trim();
    return text || fallback;
}

function columnForSelection(modelKey: ModelKey | "", selection: string) {
    if (!modelKey || !selection) return null;
    return columnsForModel(modelKey).find((column) => columnMatchesKey(column, selection)) || null;
}

/**
 * Resolves a metric filter's column. Deliberately NOT columnForSelection: that searches
 * columnsForModel (category columns only), which is the pool the breakdown and countDistinct
 * columns come from. A filter condition may sit on any visible column - numeric and date ones
 * included, which is the point of the type-aware operators - so resolving it against the
 * narrow pool would leave filterColumnId null while filterValueIds resolved fine, producing a
 * payload with values but no column. Same failure mode the selectedColumnIds note below
 * describes.
 */
function filterColumnForSelection(modelKey: ModelKey | "", selection: string) {
    if (!modelKey || !selection) return null;
    return metricFilterColumns({ model: modelKey } as MetricSection).find((column) => columnMatchesKey(column, selection)) || null;
}

function columnId(column?: DataColumn | null) {
    return column?.dataModelColumnId || null;
}

function valueIds(modelKey: ModelKey | "", columnKey: string, values: string[]) {
    const ids = resolveOptionIds(modelKey, columnKey, values);
    return ids ? ids.map((id) => (/^-?\d+$/.test(id) ? Number(id) : id)) : [];
}

function normalizeTripField(modelKey: ModelKey, columnKey: string, field: TripDataFieldState) {
    const ids = valueIds(modelKey, columnKey, field.values);
    return {
        values: field.values,
        valueIds: ids,
        freeText: field.freeText,
        contains: field.contains,
    };
}

function normalizeTripDataFilters(report: ReportConfig) {
    return {
        include: {
            logic: report.tripDataFilters.include.logic,
            logicId: logicId(report.tripDataFilters.include.logic),
            fields: TRIP_DATA_FIELDS.reduce((acc, field) => {
                const state = report.tripDataFilters.include.fields[field.key] || { values: [], freeText: "", contains: "" };
                acc[field.key] = normalizeTripField("transportation", field.column, state);
                return acc;
            }, {} as Record<string, ReturnType<typeof normalizeTripField>>),
        },
        exclude: {
            logic: report.tripDataFilters.exclude.logic,
            logicId: logicId(report.tripDataFilters.exclude.logic),
            fields: TRIP_DATA_FIELDS.reduce((acc, field) => {
                const state = report.tripDataFilters.exclude.fields[field.key] || { values: [], freeText: "", contains: "" };
                acc[field.key] = normalizeTripField("transportation", field.column, state);
                return acc;
            }, {} as Record<string, ReturnType<typeof normalizeTripField>>),
        },
    };
}

function normalizeMetricGroup(withLogicalId: WithLogicalId, section: MetricSection, group: MetricGroup, sortOrder: number) {
    const normalized = withLogicalId(group);
    return {
        ...normalized,
        valueIds: valueIds(section.model, section.breakdownColumn, normalized.values),
        sortOrder,
    };
}

function normalizeExtraBreakdown(withLogicalId: WithLogicalId, section: MetricSection, extra: ExtraBreakdown, sortOrder: number) {
    const normalized = withLogicalId(extra);
    // A Period Date part has no source column of its own; `source` tells the server to use
    // the section's periodDate expression instead.
    const column = isPeriodDateExtra(normalized) ? null : columnForSelection(section.model, normalized.column);
    return {
        ...normalized,
        dataModelColumnId: columnId(column),
        groups: normalized.groups.map((group, index) => normalizeMetricGroup(withLogicalId, section, group, (index + 1) * 10)),
        sortOrder,
    };
}

function normalizeOtpCondition(withLogicalId: WithLogicalId, section: MetricSection, condition: OtpCondition, sortOrder: number) {
    const normalized = withLogicalId(condition);
    const column = columnForSelection(section.model, normalized.column);
    return {
        ...normalized,
        dataModelColumnId: columnId(column),
        valueIds: valueIds(section.model, normalized.column, normalized.values),
        sortOrder,
    };
}

function normalizeOtpRule(withLogicalId: WithLogicalId, section: MetricSection, rule: OtpRule, sortOrder: number) {
    const normalized = withLogicalId(rule);
    return {
        ...normalized,
        conditionLogicId: logicId(normalized.conditionLogic),
        conditions: normalized.conditions.map((condition, index) => normalizeOtpCondition(withLogicalId, section, condition, (index + 1) * 10)),
        sortOrder,
    };
}

function normalizeRuleCondition(withLogicalId: WithLogicalId, report: ReportConfig, condition: RuleCondition, lookupOptionsByKey: LookupOptionsByKey, sortOrder: number) {
    const normalized = withLogicalId(condition);
    const column = findReportColumn(report, normalized.column);
    const operatorId = lookupId(lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.ruleOperator, normalized.operator, RULE_OPERATOR_FALLBACK[normalized.operator]);
    // "between"/"not_between" are the operators needing two values; everything else is
    // satisfied by one. Without this a half-filled range would be stamped isReady:true even
    // though both SQL builders correctly emit nothing for it, and the enclosing group would
    // inherit that.
    const values = (normalized.values || []).filter((value) => !!value && !!String(value).trim());
    const needsTwoValues = normalized.operator === "between" || normalized.operator === "not_between";
    const hasValue = normalized.operator === "empty"
        || normalized.operator === "not_empty"
        || (needsTwoValues ? values.length >= 2 : values.length > 0)
        || !!normalized.text.trim();

    // --- Operand modes -------------------------------------------------------------
    // A condition in either new mode is ready only once its SECOND column resolves; a
    // "minutes between" one additionally needs its number, which the hasValue check above
    // already expresses. Without this, a half-built condition - a mode picked but no second
    // column chosen yet - would be stamped isReady:true even though both SQL builders
    // correctly emit nothing for it, and its enclosing group would inherit that.
    const compareColumn = normalized.compareColumn
        ? findReportColumn(report, normalized.compareColumn)
        : null;
    const usesCompareColumn = normalized.leftKind === "minutes_between" || normalized.compareTo === "column";
    const operandReady = !usesCompareColumn
        || (!!compareColumn && (normalized.leftKind === "minutes_between" ? hasValue : true));
    // The right-hand side of a column comparison is a column, not a typed value, so the
    // value checks above do not apply to it.
    // Nor does "Compare To Today": the run date is the right-hand side.
    const valueReady = normalized.compareTo === "column" || normalized.compareTo === "today" ? true : hasValue;

    return {
        ...normalized,
        // Stamped explicitly so every saved leaf is self-describing from now on, while
        // reads still treat a missing type as a leaf (see types.ts).
        type: "condition" as const,
        isReady: !!column && !!operatorId && valueReady && operandReady,
        dataModelColumnId: column?.dataModelColumnId || null,
        operatorId,
        // Resolved alongside the column it pairs with, so a consumer reading the normalized
        // payload never has to re-resolve a client-supplied string. Null when the mode does
        // not use one, which is every condition saved before this existed.
        compareDataModelColumnId: compareColumn?.dataModelColumnId || null,
        // The RESOLVED unit, not the requested one: a date-only column pair is coerced to
        // days by both SQL builders, and a consumer reading the payload should see what will
        // actually run rather than a request that was overridden.
        differenceUnit: normalized.leftKind === "minutes_between"
            ? resolveDifferenceUnit(column, compareColumn, normalized.differenceUnit)
            : undefined,
        valueIds: column ? valueIds(column.model, column.key, normalized.values) : [],
        sortOrder,
    };
}

// A normalized node stays structurally assignable to RuleNode (so the payload's
// bucketRuleSets can still be read back as RuleSet[]) while carrying the extra resolved
// ids the saved payload needs. The leaf shape is derived from normalizeRuleCondition so
// it cannot drift from it.
type NormalizedRuleCondition = ReturnType<typeof normalizeRuleCondition>;

interface NormalizedRuleGroup extends Omit<RuleConditionGroup, "conditions"> {
    conditionLogicId: number;
    isReady: boolean;
    conditions: NormalizedRuleNode[];
    sortOrder: number;
}

type NormalizedRuleNode = (NormalizedRuleCondition | NormalizedRuleGroup) & {
    joinOperator?: "and" | "or";
    joinOperatorId?: number;
};

/**
 * Normalizes one member of a rule's condition tree. Groups recurse and carry their own
 * conditionLogicId; leaves fall through to normalizeRuleCondition unchanged, so the payload
 * shape for a flat rule is exactly what it was before nesting existed.
 *
 * `parentLogic` and `index` are needed to resolve the node's junction: the saved payload
 * records the EFFECTIVE joinOperator (plus its lookup id) rather than leaving consumers to
 * re-derive the inheritance. The first member has no junction, so it gets neither field.
 */
function normalizeRuleNode(withLogicalId: WithLogicalId,
    report: ReportConfig,
    node: RuleNode,
    lookupOptionsByKey: LookupOptionsByKey,
    sortOrder: number,
    parentLogic: "and" | "or",
    index: number,
): NormalizedRuleNode {
    const join = index === 0 ? null : ruleNodeJoin(node, parentLogic);
    const joinFields = join ? { joinOperator: join, joinOperatorId: logicId(join) } : {};

    if (!isRuleGroupNode(node)) {
        return { ...normalizeRuleCondition(withLogicalId, report, node as RuleCondition, lookupOptionsByKey, sortOrder), ...joinFields };
    }

    const normalized = withLogicalId(node);
    const groupLogic = normalized.conditionLogic;
    const children = (normalized.conditions || []).map((child, childIndex) =>
        normalizeRuleNode(withLogicalId, report, child, lookupOptionsByKey, (childIndex + 1) * 10, groupLogic, childIndex));
    return {
        ...normalized,
        ...joinFields,
        type: "group" as const,
        conditionLogicId: logicId(groupLogic),
        isReady: children.some((child) => child.isReady),
        conditions: children,
        sortOrder,
    };
}

function normalizeRule(withLogicalId: WithLogicalId, report: ReportConfig, rule: CategorizationRule, lookupOptionsByKey: LookupOptionsByKey, sortOrder: number) {
    const normalized = withLogicalId(rule);
    const targetColumn = normalized.targetField && normalized.targetField !== "category"
        ? findReportColumn(report, normalized.targetField)
        : null;
    // "Set value" source. A column-sourced rule carries its resolved catalog id (null when the
    // column no longer resolves or is not eligible - the rule is then not ready and the server
    // emits nothing for it) and drops any leftover typed value, so the stored rule says one
    // thing. A typed-value rule drops any leftover source column.
    const fromColumn = normalized.action === "set" && normalized.valueSource === "column";
    const valueColumn = fromColumn ? ruleValueColumn(report, normalized) : null;
    const valueSourceFields = fromColumn
        ? { valueSource: "column" as const, valueColumn: normalized.valueColumn || "", valueDataModelColumnId: valueColumn?.dataModelColumnId || null, valueFromList: "", valueCustom: "" }
        : { valueSource: "value" as const, valueColumn: "", valueDataModelColumnId: null };
    return {
        ...normalized,
        ...valueSourceFields,
        label: nonEmptyText(normalized.label, `Rule ${sortIndex(sortOrder)}`),
        isReady: isCategorizationRuleReady(report, normalized),
        conditionLogicId: logicId(normalized.conditionLogic),
        actionTypeId: lookupId(lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.ruleAction, normalized.action, RULE_ACTION_FALLBACK[normalized.action]),
        targetIsCategory: !normalized.targetField || normalized.targetField === "category",
        targetColumnId: targetColumn?.dataModelColumnId || null,
        conditions: normalized.conditions.map((node, index) => normalizeRuleNode(withLogicalId, report, node, lookupOptionsByKey, (index + 1) * 10, normalized.conditionLogic, index)),
        sortOrder,
    };
}

function normalizeRuleSet(withLogicalId: WithLogicalId, report: ReportConfig, ruleSet: RuleSet, lookupOptionsByKey: LookupOptionsByKey, sortOrder: number) {
    const normalized = withLogicalId(ruleSet);
    const normalizedRules = normalized.rules.map((rule, index) => normalizeRule(withLogicalId, report, rule, lookupOptionsByKey, (index + 1) * 10));
    return {
        ...normalized,
        name: nonEmptyText(normalized.name, `Categorization ${sortIndex(sortOrder)}`),
        includeElse: normalized.includeElse !== false,
        elseLabel: normalized.elseLabel?.trim() || "",
        description: (normalized as RuleSet & { description?: string }).description || "",
        isReady: normalizedRules.some((rule) => rule.isReady),
        rules: normalizedRules,
        sortOrder,
    };
}

/**
 * Normalizes one side of a metric's filter conditions into the same self-describing node
 * shape the Categorization rules use, so ReportSqlBuilder can run both through one
 * evaluator. The report handed to normalizeRuleNode is narrowed to the section's own model:
 * a metric filter must only ever resolve columns on the model it counts, even when the
 * report has several attached.
 */
function normalizeMetricFilterNodes(withLogicalId: WithLogicalId,
    report: ReportConfig,
    section: MetricSection,
    mode: "filter" | "excludeFilter",
    lookupOptionsByKey: LookupOptionsByKey,
) {
    const nodes = metricFilterNodes(section, mode);
    if (!nodes.length) return [];
    const scope: ReportConfig = { ...report, models: section.model ? [section.model] : [] };
    const logic = metricFilterLogic(section, mode);
    return nodes.map((node, index) => normalizeRuleNode(withLogicalId, scope, node, lookupOptionsByKey, (index + 1) * 10, logic, index));
}

/**
 * A metric's Period Date in the self-describing shape the server evaluates: each date column
 * carries its DataModelColumnId next to its key, and each case's conditions go through the
 * same normalizeRuleNode as metric filters - narrowed to the section's own model - so
 * ReportSqlBuilder runs them through the same RuleNodesExpression. Absent stays absent.
 */
function normalizePeriodDate(withLogicalId: WithLogicalId,
    report: ReportConfig,
    section: MetricSection,
    lookupOptionsByKey: LookupOptionsByKey,
) {
    const periodDate = section.periodDate;
    if (!periodDate) return undefined;
    const scope: ReportConfig = { ...report, models: section.model ? [section.model] : [] };
    return {
        ...periodDate,
        elseDateColumnId: columnId(periodDateColumn(section, periodDate.elseDateColumn)),
        cases: periodDate.cases.map((item, index) => {
            const normalized = withLogicalId(item);
            const logic: "and" | "or" = normalized.conditionLogic === "or" ? "or" : "and";
            return {
                ...normalized,
                conditionLogic: logic,
                conditionLogicId: logicId(logic),
                dateColumnId: columnId(periodDateColumn(section, normalized.dateColumn)),
                conditions: normalized.conditions.map((node, nodeIndex) => normalizeRuleNode(withLogicalId, scope, node, lookupOptionsByKey, (nodeIndex + 1) * 10, logic, nodeIndex)),
                sortOrder: (index + 1) * 10,
            };
        }),
    };
}

/**
 * A metric's own reporting-period filter, conditions normalized exactly like a Period Date
 * case so ReportSqlBuilder runs them through RuleNodesExpression. Mode "report" (or absent)
 * is dropped - the server then applies the report's period, as for every older config.
 */
function normalizeMetricPeriodFilter(withLogicalId: WithLogicalId,
    report: ReportConfig,
    section: MetricSection,
    lookupOptionsByKey: LookupOptionsByKey,
) {
    const filter = section.periodFilter;
    if (filter?.mode !== "custom") return undefined;
    const scope: ReportConfig = { ...report, models: section.model ? [section.model] : [] };
    const logic: "and" | "or" = filter.conditionLogic === "or" ? "or" : "and";
    return {
        mode: "custom" as const,
        conditionLogic: logic,
        conditionLogicId: logicId(logic),
        conditions: (filter.conditions || []).map((node, nodeIndex) => normalizeRuleNode(withLogicalId, scope, node, lookupOptionsByKey, (nodeIndex + 1) * 10, logic, nodeIndex)),
    };
}

/**
 * Only explicit "off" entries for models still on the report are written; absent means the
 * reporting period applies (ReportSqlBuilder.ModelAppliesReportingPeriod), so a report that
 * never touched the switch saves exactly as before.
 */
function normalizeModelApplyReportingPeriod(report: ReportConfig) {
    const flags = report.modelApplyReportingPeriod;
    if (!flags) return undefined;
    const off = report.models.filter((modelKey) => flags[modelKey] === false);
    if (!off.length) return undefined;
    return off.reduce<Partial<Record<ModelKey, boolean>>>((acc, modelKey) => {
        acc[modelKey] = false;
        return acc;
    }, {});
}

/**
 * The Report-level "if blank" column carries its DataModelColumnId next to its key, like
 * every other column reference the server resolves. Bounds for models no longer on the
 * report are dropped.
 */
function normalizeModelPeriodBounds(report: ReportConfig) {
    const bounds = report.modelPeriodBounds;
    if (!bounds) return undefined;
    return report.models.reduce((acc, modelKey) => {
        const item = bounds[modelKey];
        if (!item) return acc;
        const dateColumnId = (key?: string) => key ? columnId(timestampColumnsForModel(modelKey).find((column) => columnMatchesKey(column, key))) : undefined;
        // Each bound's fallback is written out explicitly (the legacy shared ifBlank folded in),
        // with its column's DataModelColumnId, so the server never has to fall back itself.
        const normalizeIfBlank = (ifBlank?: PeriodIfBlank) => ifBlank && ifBlank.kind !== "none"
            ? { ...ifBlank, columnId: ifBlank.kind === "column" ? dateColumnId(ifBlank.column) : undefined }
            : undefined;
        const { ifBlank: legacyIfBlank, ...rest } = item;
        acc[modelKey] = {
            ...rest,
            startColumnId: dateColumnId(item.startColumn),
            endColumnId: dateColumnId(item.endColumn),
            startIfBlank: normalizeIfBlank(item.startIfBlank || legacyIfBlank),
            endIfBlank: normalizeIfBlank(item.endIfBlank || legacyIfBlank),
        };
        return acc;
    }, {} as Record<string, unknown>);
}

/**
 * The Co-Plan client ids persisted for one metric: positive integers, de-duplicated, in
 * the order the user picked them, with the report plan's OWN client removed.
 *
 * Dropping the plan's client is the point, not tidying: the backend
 * (ReportSqlBuilder.ClientScopeClause) unions the plan's current client in at query time,
 * so a stored copy of it is redundant while the report stays put and actively wrong once
 * the report is cloned into another plan - it would keep widening every query to the old
 * plan's rows. The picker shows that client selected and disabled for the same reason.
 *
 * The ids are re-validated server-side against active ClientInfos, which rejects the save
 * rather than storing a scope that names a client that does not exist.
 */
function normalizeCoPlanClientIds(plan: PlanConfig, section: MetricSection) {
    const planClientId = Number(plan.clientId) || 0;
    const seen = new Set<number>();
    return (section.coPlanClientIds || []).reduce<number[]>((ids, value) => {
        const id = Number(value);
        if (!Number.isInteger(id) || id <= 0 || id === planClientId || seen.has(id)) return ids;
        seen.add(id);
        ids.push(id);
        return ids;
    }, []);
}

function normalizeMetricSection(withLogicalId: WithLogicalId, plan: PlanConfig, report: ReportConfig, section: MetricSection, dataModels: Record<ModelKey, DataModel>, lookupOptionsByKey: LookupOptionsByKey, sortOrder: number) {
    const normalized = withLogicalId(section);
    const model = normalized.model ? report.models.find((modelKey) => modelKey === normalized.model) : "";
    const breakdownColumn = columnForSelection(normalized.model, normalized.breakdownColumn);
    const countDistinctColumn = columnForSelection(normalized.model, normalized.countDistinctColumn);
    const filterColumn = filterColumnForSelection(normalized.model, normalized.filterColumn);
    const excludeFilterColumn = filterColumnForSelection(normalized.model, normalized.excludeFilterColumn);
    const ruleSet = report.bucketRuleSets.find((set) => set.id === normalized.ruleSetId);
    const metricName = nonEmptyText(normalized.name, breakdownColumn?.label ? `Count By ${breakdownColumn.label}` : `Metric ${sortIndex(sortOrder)}`);
    const metricForReadiness = { ...normalized, name: metricName } as MetricSection;
    // reconcileExcelLayoutColumns is otherwise only called from inside ExcelLayoutEditor,
    // triggered by the user interacting with THAT tab (toggling a column, applying a
    // template, adding a time axis, etc). If someone expands their column selection on
    // the Manage Columns tab and never reopens the Excel Layout tab afterward, the
    // layout persisted here would - without this call - still be whatever smaller
    // column set existed the last time that tab was touched: every newly-selected
    // column would have no `columns[]` entry at all (so no Include:true), silently
    // capping the downloaded sheet at however many columns existed back then, even
    // though selectedColumnIds/the SQL now correctly return the full new selection.
    // Reconciling here at save time - the same sync the tab does reactively - keeps the
    // persisted layout an accurate reflection of the current selection unconditionally.
    const excelLayout = normalized.excelLayout
        ? { ...reconcileExcelLayoutColumns(normalized, normalized.excelLayout, report), saveAsTemplate: false }
        : normalized.excelLayout;
    return {
        ...normalized,
        excelLayout,
        name: metricName,
        isReady: isMetricReady(report, metricForReadiness),
        model,
        dataModelId: normalized.model ? getDataModelId(dataModels, normalized.model) : null,
        breakdownColumnId: columnId(breakdownColumn),
        displayTypeId: lookupId(lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.metricDisplay, normalized.displayAs, DISPLAY_TYPE_FALLBACK[normalized.displayAs]),
        countDistinctColumnId: columnId(countDistinctColumn),
        includeValueIds: valueIds(normalized.model, normalized.breakdownColumn, normalized.include),
        excludeValueIds: valueIds(normalized.model, normalized.breakdownColumn, normalized.exclude),
        groups: normalized.groups.map((group, index) => normalizeMetricGroup(withLogicalId, normalized, group, (index + 1) * 10)),
        extraBreakdowns: normalized.extraBreakdowns.map((extra, index) => normalizeExtraBreakdown(withLogicalId, normalized, extra, (index + 1) * 10)),
        periodDate: normalizePeriodDate(withLogicalId, report, normalized, lookupOptionsByKey),
        periodFilter: normalizeMetricPeriodFilter(withLogicalId, report, normalized, lookupOptionsByKey),
        // NOTE: deliberately NOT columnForSelection (that resolves against columnsForModel,
        // the category-columns-only pool used for breakdown/filter/countDistinct columns).
        // A detail metric's selectedColumns can be ANY visible column - timestamp, id, etc,
        // not just category-role ones - so this resolves against the same detailColumnsForModel
        // pool the Excel Layout tab / excelDetailSectionAliases / buildSqlForSection's "detail"
        // branch already use. Using columnForSelection here silently dropped every selected
        // non-category column from selectedColumnIds, which the server-side SQL builder relies
        // on - it showed correctly in the Excel Layout tab (built from the same full pool) but
        // went missing from the actual generated workbook.
        selectedColumnIds: normalized.selectedColumns
            .map((selection) => columnId(findSelectedDetailColumn(detailColumnsForModel(normalized.model), selection)))
            .filter((id): id is number => !!id),
        coPlanClientIds: normalizeCoPlanClientIds(plan, normalized),
        filterColumnId: columnId(filterColumn),
        filterValueIds: valueIds(normalized.model, normalized.filterColumn, normalized.filterValues),
        excludeFilterColumnId: columnId(excludeFilterColumn),
        excludeFilterValueIds: valueIds(normalized.model, normalized.excludeFilterColumn, normalized.excludeFilterValues),
        // Always emitted, even for a section that has only ever used the legacy single-column
        // fields: metricFilterNodes synthesises the equivalent one-condition tree, so the
        // server sees one shape for every config instead of having to reimplement the
        // legacy-to-tree fallback itself. The legacy fields above are still written (see the
        // MetricSection note in types.ts), so nothing that reads only those breaks.
        filterLogic: metricFilterLogic(normalized, "filter"),
        filterConditions: normalizeMetricFilterNodes(withLogicalId, report, normalized, "filter", lookupOptionsByKey),
        excludeFilterLogic: metricFilterLogic(normalized, "excludeFilter"),
        excludeFilterConditions: normalizeMetricFilterNodes(withLogicalId, report, normalized, "excludeFilter", lookupOptionsByKey),
        otpRules: normalized.otpRules.map((rule, index) => normalizeOtpRule(withLogicalId, normalized, rule, (index + 1) * 10)),
        otpMissingHandlingId: lookupId(lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.otpMissingHandling, normalized.otpMissingHandling, OTP_MISSING_FALLBACK[normalized.otpMissingHandling]),
        // A combined metric keeps its last single rule set as a draft (see switchBreakdownSource);
        // it is not this metric's rule set, so the rc.Metric mirror must not record it.
        ruleSetLogicalId: usesCombinedRuleSets(normalized) ? null : ruleSet?.logicalId || null,
        // Combined rule sets in order, for rc.usp_SaveMetrics v6 -> rc.MetricRuleSet (same shape
        // as detailRuleSetColumns). Omitted for every other metric, so their payload is unchanged.
        combinedRuleSetColumns: usesCombinedRuleSets(normalized)
            ? combinedRuleSetIds(normalized).flatMap((id) => {
                const combinedRuleSet = report.bucketRuleSets.find((set) => set.id === id);
                return combinedRuleSet?.logicalId ? [{ ruleSetLogicalId: combinedRuleSet.logicalId, columnName: (combinedRuleSet.name || "").trim() || "Category" }] : [];
            })
            : undefined,
        // Full Row Detail rule-set list in selection order, with each column's name, for
        // rc.usp_SaveMetrics v5 -> rc.MetricRuleSet. Omitted (undefined) for every metric that
        // does not use the list, so their payload is unchanged.
        detailRuleSetColumns: usesDetailRuleSetList(normalized)
            ? selectedDetailRuleSetIds(normalized).flatMap((id) => {
                const selectedRuleSet = report.bucketRuleSets.find((set) => set.id === id);
                return selectedRuleSet?.logicalId
                    ? [{ ruleSetLogicalId: selectedRuleSet.logicalId, columnName: detailRuleSetDisplayName(normalized, id, selectedRuleSet.name) }]
                    : [];
            })
            : undefined,
        sortOrder,
        generatedSqlText: buildSqlForSection(plan, report, metricForReadiness, true),
    };
}

function getDataModelId(dataModels: Record<ModelKey, DataModel>, modelKey: ModelKey) {
    return dataModels[modelKey]?.dataModelId;
}

function normalizeReportDataModels(report: ReportConfig, dataModels: Record<ModelKey, DataModel>) {
    return report.models.map((modelKey, index) => {
        const model = dataModels[modelKey];
        const modelColumns = columnsForModel(modelKey);
        const basedOnColumn = findTimestampColumn(model, report.modelBasedOn[modelKey])
            || modelColumns.find((column) => columnMatchesKey(column, report.modelBasedOn[modelKey] || ""));
        return {
            modelKey,
            dataModelId: getDataModelIdFromReport(report, dataModels, modelKey),
            basedOnColumn: report.modelBasedOn[modelKey] || dataColumnSelectionKey(basedOnColumn),
            basedOnColumnId: columnId(basedOnColumn),
            sortOrder: (index + 1) * 10,
        };
    }).filter((item) => !!item.dataModelId);
}

function getDataModelIdFromReport(report: ReportConfig, dataModels: Record<ModelKey, DataModel>, modelKey: ModelKey) {
    const index = report.models.indexOf(modelKey);
    return getDataModelId(dataModels, modelKey) || report.dataModelIds?.[index] || undefined;
}

function syncReportDataModelIds(report: ReportConfig, dataModels: Record<ModelKey, DataModel>) {
    return {
        ...report,
        dataModelIds: report.models.map((modelKey) => getDataModelIdFromReport(report, dataModels, modelKey)).filter((id): id is number => !!id),
    };
}

export function buildReportConfigurationPayload(withLogicalId: WithLogicalId, args: BuildPayloadArgs): BuildPayloadResult {
    const plan = withLogicalId(clone(args.plan));
    const folder = withLogicalId(clone(args.folder));
    const reportBase = withLogicalId(syncReportDataModelIds(clone(args.report), args.dataModels));
    const reportWithRuleSetIds = {
        ...reportBase,
        bucketRuleSets: reportBase.bucketRuleSets.map(withLogicalId),
    };
    const normalizedRuleSets = reportWithRuleSetIds.bucketRuleSets.map((ruleSet, index) => normalizeRuleSet(withLogicalId, reportWithRuleSetIds, ruleSet, args.lookupOptionsByKey, (index + 1) * 10));
    const report: ReportConfig = {
        ...reportWithRuleSetIds,
        bucketRuleSets: normalizedRuleSets as RuleSet[],
    };
    const sections = report.sections.map((section, index) => normalizeMetricSection(withLogicalId, plan, report, section, args.dataModels, args.lookupOptionsByKey, (index + 1) * 10));
    const dataModels = normalizeReportDataModels(report, args.dataModels);
    const generatedQueries = sections.map((section, index) => ({
        metricId: section.id,
        metricLogicalId: section.logicalId,
        queryName: section.name,
        sqlText: section.generatedSqlText,
        plainEnglishText: buildPlainEnglish(plan, { ...report, sections: [section as MetricSection] }, args.lookupValues, args.clientOptions),
        generatorVersion: "frontend-v1",
        sortOrder: (index + 1) * 10,
    }));
    const frequencyTypeId = lookupId(args.lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.frequency, report.frequency);
    const reportingPeriodType = normalizeReportingPeriodType(report.reportingPeriod.type);
    const activeSheets = excelSheetsInUse(report);
    const activeSheetKeys = new Set(activeSheets.map((sheet) => sheet.key));
    const sheetOrder = activeSheets.map((sheet) => sheet.key);
    const sheetHeaders = Object.entries(report.excelWorkbook?.sheetHeaders || {}).reduce((acc, [key, value]) => {
        if (activeSheetKeys.has(key)) acc[key] = value;
        return acc;
    }, {} as Record<string, WorkbookExcelSheetHeader>);
    const legacyWorkbookHeader = report.excelWorkbook?.header;
    const firstSheet = activeSheets[0];
    if (legacyWorkbookHeader?.enabled && firstSheet && !sheetHeaders[firstSheet.key]?.header) {
        const currentSheetHeader = sheetHeaders[firstSheet.key];
        sheetHeaders[firstSheet.key] = {
            enabled: currentSheetHeader?.enabled ?? false,
            titleTemplate: currentSheetHeader?.titleTemplate ?? report.excelWorkbook?.titleTemplate ?? "",
            header: legacyWorkbookHeader,
        };
    }
    const excelWorkbook = report.excelWorkbook
        ? {
            titleTemplate: report.excelWorkbook.titleTemplate || "",
            sheetHeaders,
            sheetOrder,
        }
        : report.excelWorkbook;

    const payloadReport = {
        ...report,
        excelWorkbook,
        frequencyTypeId,
        globalFilters: {
            ...report.globalFilters,
            tripActivityId: lookupId(args.lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.tripActivity, report.globalFilters.tripActivity, TRIP_ACTIVITY_FALLBACK[report.globalFilters.tripActivity]),
        },
        dataModels,
        modelPeriodBounds: normalizeModelPeriodBounds(report),
        modelApplyReportingPeriod: normalizeModelApplyReportingPeriod(report),
        reportingPeriod: {
            ...report.reportingPeriod,
            type: reportingPeriodType,
            reportingPeriodTypeId: lookupId(args.lookupOptionsByKey, STATIC_LOOKUP_ENTITY_KEYS.reportingPeriod, reportingPeriodType),
        },
        bucketRuleSets: normalizedRuleSets,
        sections: sections.map(({ generatedSqlText, ...section }) => section),
        tripDataFilters: normalizeTripDataFilters(report),
        callCenterSla: {
            ...report.callCenterSla,
            countDistinctColumnId: columnId(columnForSelection("callcenter", report.callCenterSla.countDistinctColumn)),
        },
    };

    const payload = {
        schemaVersion: SCHEMA_VERSION,
        configVersionId: report.configVersionId ?? null,
        operation: report.configVersionId ? "updateDraft" : "saveDraft",
        clientId: plan.clientId ?? null,
        reportPlanId: plan.reportPlanId ?? null,
        reportFolderId: folder.reportFolderId ?? null,
        reportId: report.reportId ?? null,
        reportName: report.label,
        isDraft: true,
        isFinalized: false,
        audit: {
            savedBy: "server-user",
            savedUtc: "server-time",
        },
        configJson: {
            plan,
            folder,
            report: payloadReport,
            generatedQueries,
        },
    };

    return {
        payload,
        plan,
        folder,
        report: payloadReport as ReportConfig,
    };
}

export function rebindReportConfigurationPayload(
    currentPlan: PlanConfig,
    currentFolder: ReportFolder,
    currentReport: ReportConfig,
    detail: ReportConfigurationConfigVersionDetailDto,
) {
    const configJson = detail.configJson || {};
    const savedPlan = configJson.plan || {};
    const savedFolder = configJson.folder || {};
    const savedReport = (configJson.report || {}) as Partial<ReportConfig>;
    const legacySavedPlan = savedPlan as Partial<PlanConfig> & {
        tripActivity?: ReportConfig["globalFilters"]["tripActivity"];
        exclusions?: ReportConfig["globalFilters"]["exclusions"];
    };
    const legacyPlanFilters = {
        tripActivity: legacySavedPlan.tripActivity,
        exclusions: legacySavedPlan.exclusions,
    };

    const nextReport: ReportConfig = {
        ...currentReport,
        ...clone(savedReport),
        id: currentReport.id,
        reportId: currentReport.reportId,
        reportFolderId: currentReport.reportFolderId,
        reportKey: currentReport.reportKey,
        isBuiltIn: currentReport.isBuiltIn,
        sortOrder: currentReport.sortOrder,
        configVersionId: detail.configVersionId,
        globalFilters: {
            tripActivity: savedReport.globalFilters?.tripActivity || legacyPlanFilters.tripActivity || currentReport.globalFilters?.tripActivity || "active-only",
            exclusions: {
                memberIds: Array.isArray(savedReport.globalFilters?.exclusions?.memberIds)
                    ? [...savedReport.globalFilters.exclusions.memberIds]
                    : Array.isArray(legacyPlanFilters.exclusions?.memberIds)
                        ? [...legacyPlanFilters.exclusions.memberIds]
                        : currentReport.globalFilters?.exclusions?.memberIds || [],
                invoiceNumbers: Array.isArray(savedReport.globalFilters?.exclusions?.invoiceNumbers)
                    ? [...savedReport.globalFilters.exclusions.invoiceNumbers]
                    : Array.isArray(legacyPlanFilters.exclusions?.invoiceNumbers)
                        ? [...legacyPlanFilters.exclusions.invoiceNumbers]
                        : currentReport.globalFilters?.exclusions?.invoiceNumbers || [],
            },
        },
    } as ReportConfig;

    const nextFolder: ReportFolder = {
        ...currentFolder,
        ...clone(savedFolder),
        id: currentFolder.id,
        reportFolderId: currentFolder.reportFolderId,
        reportPlanId: currentFolder.reportPlanId,
        folderKey: currentFolder.folderKey,
        isBuiltIn: currentFolder.isBuiltIn,
        sortOrder: currentFolder.sortOrder,
        reports: currentFolder.reports.map((report) => report.id === currentReport.id ? nextReport : report),
    };

    const nextPlan: PlanConfig = {
        ...currentPlan,
        ...clone(savedPlan),
        id: currentPlan.id,
        reportPlanId: currentPlan.reportPlanId,
        planKey: currentPlan.planKey,
        planName: currentPlan.planName,
        clientId: currentPlan.clientId,
        clientName: currentPlan.clientName,
        reportFolders: currentPlan.reportFolders.map((folder) => folder.id === currentFolder.id ? nextFolder : folder),
    };

    return {
        plan: nextPlan,
        folder: nextFolder,
        report: nextReport,
    };
}

export type WithLogicalId = <T extends { logicalId?: string }>(value: T) => T;
